/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */

/** @module react-icons/utils/icon */
import { mergeClassNames, mergeStyles, ImgHoverSwitch, AnchorWrapper } from '@codistica/react';
import React from 'react';
import resetClassNames from '../css/reset.module.scss';

/**
 * @typedef iconPropsType
 * @property {string} [height=40] - Icon height.
 * @property {string} [width='auto'] - Icon width.
 * @property {(string|null)} [href=null] - Social media link.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description Icon icon component.
 * @param {iconPropsType} props - Props.
 * @returns {Object<string,*>} React component.
 */
function Icon(props) {
  var defaultImg = props.defaultImg,
      onHoverImg = props.onHoverImg,
      height = props.height,
      width = props.width,
      href = props.href,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme;
  var globalStyles = globalTheme ? Icon.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? Icon.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style)
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, globalClassNames.root, customClassNames.root, className)
  };
  return /*#__PURE__*/React.createElement(AnchorWrapper, {
    href: href
  }, /*#__PURE__*/React.createElement(ImgHoverSwitch, {
    defaultImg: defaultImg,
    onHoverImg: onHoverImg,
    disable: !href,
    tabIndex: -1,
    customStyles: {
      root: mergedStyles.root,
      img: !height && !width ? {
        height: 40,
        width: 'auto'
      } : {
        height: height ? height : 'auto',
        width: width ? width : 'auto'
      }
    },
    customClassNames: {
      root: mergedClassNames.root
    }
  }));
}

Icon.defaultProps = {
  height: undefined,
  width: undefined,
  href: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
Icon.globalStyles = {
  default: {
    root: {}
  }
};
Icon.globalClassNames = {
  default: {
    root: ''
  }
};
export { Icon };